/**
 * Eine Prüfung einrichten.
 *
 * Drei Felder: Objekt, Jahr, E-Mail des Beirats. Mehr soll der Hausverwalter
 * nicht ausfüllen müssen — alles Weitere steht in iDWELL und wird von dort
 * geholt.
 *
 * Zwei Dinge sind bewusst so gebaut:
 *
 * Das Jahr ist eine Auswahl aus den letzten Jahren, kein Datumsbereich. Eine
 * Beiratsprüfung läuft über ein Kalenderjahr; ein freies Von-Bis lädt zum
 * Vertippen ein und bringt keinen Nutzen. Gefiltert wird nach dem
 * Rechnungsdatum, nicht nach dem Importdatum — das ist nicht dasselbe, und
 * wer es verwechselt, prüft das falsche Jahr.
 *
 * Der Einladungslink erscheint genau einmal, nach dem Anlegen. Gespeichert
 * ist nur sein Hash; aus der Datenbank ist er nicht wiederherstellbar. Das
 * steht auch so auf dem Schirm, damit niemand das Fenster schließt und sich
 * danach wundert.
 */

import { useEffect, useState } from 'react';

import { api, ApiFehler, eur } from '../lib/api';

interface Objekt {
  id: string;
  nummer: string;
  bezeichnung: string;
  ort: string | null;
}

interface Ergebnis {
  id: string;
  liegenschaft: { id: string; nummer: string; bezeichnung: string };
  jahr: number;
  belegquelle: 'idwell' | 'import';
  belege: number;
  ohneKontierung: number;
  summe: number;
  einladung: { name: string; email: string; link: string } | null;
}

export interface EinrichtenProps {
  /** Wird mit der Kennung der neuen Prüfung gerufen. */
  onFertig: (pruefungId: string) => void;
  /** Im Dialogfenster: schmalerer Rahmen, Schließen-Knopf. */
  imDialog?: boolean;
  onSchliessen?: () => void;
  /** Ob die Hausverwaltung an iDWELL hängt. Ohne Schlüssel bleibt nur das Importpaket. */
  idwellVerbunden?: boolean;
}

export function Einrichten({ onFertig, imDialog, onSchliessen, idwellVerbunden = true }: EinrichtenProps) {
  const [quelle, setQuelle] = useState<'idwell' | 'import'>(idwellVerbunden ? 'idwell' : 'import');
  const [objekte, setObjekte] = useState<Objekt[] | null>(null);
  const [objektId, setObjektId] = useState('');
  const [bezeichnung, setBezeichnung] = useState('');
  const [nummer, setNummer] = useState('');
  // Das Vorjahr: eine Beiratsprüfung läuft über ein abgeschlossenes Jahr.
  const [jahr, setJahr] = useState(new Date().getFullYear() - 1);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [ergebnis, setErgebnis] = useState<Ergebnis | null>(null);
  const [kopiert, setKopiert] = useState(false);

  useEffect(() => {
    if (!idwellVerbunden) {
      setObjekte([]);
      return;
    }
    api
      .objekte()
      .then((liste) => {
        setObjekte(liste);
        if (liste.length === 1) setObjektId(liste[0].id);
      })
      .catch((grund) => {
        setObjekte([]);
        setFehler(
          grund instanceof ApiFehler && grund.status === 502
            ? 'iDWELL ist nicht erreichbar. Ist der API-Schlüssel in den Einstellungen hinterlegt?'
            : (grund as Error).message,
        );
      });
  }, [idwellVerbunden]);

  const bereit = quelle === 'idwell' ? Boolean(objektId) : Boolean(bezeichnung.trim());

  async function anlegen(e: React.FormEvent) {
    e.preventDefault();
    if (!bereit || laeuft) return;

    setLaeuft(true);
    setFehler(null);
    try {
      const antwort = await api.einrichten(
        quelle === 'idwell'
          ? { liegenschaftId: objektId, jahr, belegquelle: 'idwell' }
          : { liegenschaftId: nummer.trim() || undefined, bezeichnung: bezeichnung.trim(), jahr, belegquelle: 'import' },
        // Der Beirat wird im nächsten Schritt eingeladen, damit der
        // Mailversand dort scheitern darf, ohne das Anlegen mitzureißen.
      );
      setErgebnis(antwort);
    } catch (grund) {
      setFehler((grund as Error).message);
    } finally {
      setLaeuft(false);
    }
  }

  // ── Nach dem Anlegen ─────────────────────────────────────
  if (ergebnis) {
    return (
      <div className={imDialog ? '' : 'seite-schmal'}>
        <h2 className="sec" style={imDialog ? { marginTop: 0 } : undefined}>
          Prüfung angelegt
        </h2>

        <div className="card" style={{ padding: 18 }}>
          <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>
            {ergebnis.liegenschaft.nummer ? `${ergebnis.liegenschaft.nummer} — ` : ''}
            {ergebnis.liegenschaft.bezeichnung}
          </h3>
          <p style={{ margin: 0, color: 'var(--tx-2)', fontSize: 13 }}>
            {ergebnis.belegquelle === 'idwell'
              ? `Abrechnung ${ergebnis.jahr} · ${ergebnis.belege} Belege aus iDWELL · ${eur(ergebnis.summe)}`
              : `Abrechnung ${ergebnis.jahr} · Belege kommen aus dem Importpaket (Buchungsliste + Belegordner)`}
          </p>

          {ergebnis.ohneKontierung > 0 && (
            <p className="note">
              {ergebnis.ohneKontierung}{' '}
              {ergebnis.ohneKontierung === 1 ? 'Beleg hat' : 'Belege haben'} keine Buchungszeile
              und {ergebnis.ohneKontierung === 1 ? 'erscheint' : 'erscheinen'} in einer eigenen
              Gruppe am Ende.
            </p>
          )}
        </div>

        <Einladen
          pruefungId={ergebnis.id}
          liegenschaft={ergebnis.liegenschaft.bezeichnung}
          jahr={ergebnis.jahr}
          vorbelegt={ergebnis.einladung}
        />

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button className="btn primary" onClick={() => onFertig(ergebnis.id)}>
            Weiter zu den Unterlagen
          </button>
          {imDialog && (
            <button className="btn" onClick={onSchliessen}>
              Schließen
            </button>
          )}
          <button
            className="btn"
            onClick={() => {
              setErgebnis(null);
              setEmail('');
              setName('');
            }}
          >
            Noch eine anlegen
          </button>
        </div>
      </div>
    );
  }

  // ── Das Formular ─────────────────────────────────────────
  const jahre = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  return (
    <div className={imDialog ? '' : 'seite-schmal'}>
      <h2 className="sec" style={imDialog ? { marginTop: 0 } : undefined}>
        Belegprüfung einrichten
        <span className="eyebrow" style={{ fontWeight: 500 }}>
          Objekt, Jahr, Beirat
        </span>
      </h2>

      <form className="card" style={{ padding: 18 }} onSubmit={anlegen}>
        <div className="feld">
          <span>Belegquelle</span>
          <div className="seg" role="group" aria-label="Belegquelle">
            <button
              type="button"
              aria-pressed={quelle === 'idwell'}
              disabled={!idwellVerbunden}
              onClick={() => setQuelle('idwell')}
              title={idwellVerbunden ? undefined : 'Kein iDWELL-Schlüssel hinterlegt'}
            >
              iDWELL
            </button>
            <button type="button" aria-pressed={quelle === 'import'} onClick={() => setQuelle('import')}>
              Importpaket
            </button>
          </div>
          <small>
            {quelle === 'idwell'
              ? 'Belege und Buchungszeilen kommen aus iDWELL.'
              : 'Belege und Buchungen kommen aus einer Buchungsliste (CSV) mit Belegordner, z. B. dem Export „Buchungen nach Konten".'}
          </small>
        </div>

        {quelle === 'import' && (
          <>
            <label className="feld">
              <span>Objekt</span>
              <input
                type="text"
                value={bezeichnung}
                onChange={(e) => setBezeichnung(e.target.value)}
                placeholder="z. B. WEG Musterweg 1-3"
                required
              />
            </label>
            <label className="feld">
              <span>Objektnummer</span>
              <input type="text" value={nummer} onChange={(e) => setNummer(e.target.value)} placeholder="optional" />
            </label>
          </>
        )}

        {quelle === 'idwell' && (
        <label className="feld">
          <span>Objekt</span>
          {objekte === null ? (
            <div style={{ color: 'var(--tx-2)', fontSize: 13, padding: '8px 0' }}>
              Objekte werden aus iDWELL geladen …
            </div>
          ) : objekte.length === 0 ? (
            <div style={{ color: 'var(--warn)', fontSize: 13, padding: '8px 0' }}>
              Keine Objekte gefunden.
            </div>
          ) : (
            <select value={objektId} onChange={(e) => setObjektId(e.target.value)} required>
              <option value="">Bitte wählen</option>
              {objekte.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.nummer} — {o.bezeichnung}
                  {o.ort ? `, ${o.ort}` : ''}
                </option>
              ))}
            </select>
          )}
        </label>
        )}

        <label className="feld">
          <span>Jahr</span>
          <select value={jahr} onChange={(e) => setJahr(Number(e.target.value))}>
            {jahre.map((j) => (
              <option key={j} value={j}>
                {j}
              </option>
            ))}
          </select>
          <small>
            Nach Rechnungsdatum, nicht nach Importdatum — eine Rechnung aus {jahr} zählt auch
            dann, wenn sie erst später erfasst wurde.
          </small>
        </label>

        <label className="feld">
          <span>E-Mail des Beirats</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="beirat@example.at"
          />
          <small>Leer lassen, um die Prüfung erst vorzubereiten und später einzuladen.</small>
        </label>

        {email.trim() && (
          <label className="feld">
            <span>Name des Beirats</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="optional"
            />
          </label>
        )}

        {fehler && (
          <p style={{ color: 'var(--warn)', fontSize: 13, margin: '4px 0 12px' }}>{fehler}</p>
        )}

        <button className="btn primary" type="submit" disabled={!bereit || laeuft}>
          {laeuft ? 'Belege werden geholt …' : 'Prüfung anlegen'}
        </button>

        <p className="note">
          {quelle === 'idwell'
            ? 'Beim Anlegen werden die Belege des Jahres aus iDWELL geholt und zwischengespeichert. Geändert wird in iDWELL nichts.'
            : 'Das Importpaket laden Sie im nächsten Schritt hoch — zusammen mit den übrigen Unterlagen.'}
        </p>
      </form>
    </div>
  );
}


/**
 * Einen Beirat einladen und den Link verschicken.
 *
 * Eigener Schritt, nicht Teil des Anlegens. Der Mailversand hängt an einem
 * SMTP-Zugang, der fehlen oder klemmen kann; dass daran das Anlegen der
 * Prüfung scheitert, wäre das falsche Verhalten. Scheitert der Versand,
 * steht der Link trotzdem da und lässt sich von Hand verschicken.
 *
 * Mehrere Beiräte nacheinander sind der Normalfall — in einer WEG sitzen
 * meist zwei oder drei im Beirat. Deshalb bleibt das Feld nach dem Senden
 * stehen und die Versendeten sammeln sich darunter.
 */
export function Einladen({
  pruefungId,
  liegenschaft,
  jahr,
  vorbelegt,
  onEingeladen,
}: {
  pruefungId: string;
  liegenschaft: string;
  jahr: number;
  vorbelegt: { name: string; email: string; link: string } | null;
  onEingeladen?: () => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [mailBereit, setMailBereit] = useState<{ ok: boolean; meldung: string } | null>(null);
  const [kopiert, setKopiert] = useState<string | null>(null);
  const [versendet, setVersendet] = useState<
    { name: string; email: string; link: string; verschickt: boolean; meldung: string }[]
  >(
    vorbelegt
      ? [{ ...vorbelegt, verschickt: false, meldung: 'Beim Anlegen erzeugt, noch nicht verschickt.' }]
      : [],
  );

  useEffect(() => {
    api
      .mailStatus()
      .then(setMailBereit)
      .catch(() => setMailBereit({ ok: false, meldung: 'Der Mailstatus ist nicht abrufbar.' }));
  }, []);

  async function einladen(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || laeuft) return;
    setLaeuft(true);
    setFehler(null);
    try {
      const antwort = await api.einladen(pruefungId, email.trim(), name.trim() || undefined);
      setVersendet((alt) => [...alt, antwort]);
      setEmail('');
      setName('');
      onEingeladen?.();
    } catch (grund) {
      setFehler((grund as Error).message);
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="card" style={{ padding: 18, marginTop: 14 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 14 }}>Beirat einladen</h3>
      <p style={{ margin: '0 0 12px', color: 'var(--tx-2)', fontSize: 12.5 }}>
        {mailBereit === null
          ? 'Mailversand wird geprüft …'
          : mailBereit.ok
            ? 'Der Link wird per Mail verschickt.'
            : `Kein Mailversand: ${mailBereit.meldung} Der Link lässt sich kopieren.`}
      </p>

      <form onSubmit={einladen}>
        <label className="feld">
          <span>E-Mail</span>
          <input
            type="email"
            required
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
            placeholder="beirat@example.at"
          />
        </label>
        <label className="feld">
          <span>Name</span>
          <input
            type="text"
            value={name}
            onChange={(ev) => setName(ev.target.value)}
            placeholder="optional"
          />
        </label>

        {fehler && (
          <p style={{ color: 'var(--warn)', fontSize: 13, margin: '0 0 10px' }}>{fehler}</p>
        )}

        <button className="btn primary" type="submit" disabled={!email.trim() || laeuft}>
          {laeuft ? 'Wird verschickt …' : 'Einladen'}
        </button>
      </form>

      {versendet.length > 0 && (
        <div style={{ marginTop: 16, borderTop: '1px solid var(--line)', paddingTop: 14 }}>
          {versendet.map((v, i) => (
            <div key={i} style={{ marginBottom: i === versendet.length - 1 ? 0 : 14 }}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
              >
                <strong style={{ fontSize: 13 }}>{v.name}</strong>
                <span style={{ fontSize: 12.5, color: 'var(--tx-2)' }}>{v.email}</span>
                <span className={`pill ${v.verschickt ? 'ok' : 'w'}`}>
                  <span className="dot" />
                  {v.verschickt ? 'verschickt' : 'nicht verschickt'}
                </span>
              </div>
              <p style={{ margin: '4px 0 6px', fontSize: 12, color: 'var(--tx-2)' }}>{v.meldung}</p>

              {/* Der Link steht immer da — auch nach erfolgreichem Versand.
                  Mails verschwinden in Spam-Ordnern, und der Klartext
                  existiert nur in dieser einen Antwort. */}
              <div
                className="mono"
                style={{
                  padding: '8px 10px',
                  background: 'var(--sunk)',
                  border: '1px solid var(--line)',
                  borderRadius: 'var(--r-s)',
                  fontSize: 11,
                  wordBreak: 'break-all',
                }}
              >
                {v.link}
              </div>
              <button
                className="btn ghost sm"
                style={{ marginTop: 6 }}
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(v.link);
                    setKopiert(v.link);
                    setTimeout(() => setKopiert(null), 2000);
                  } catch {
                    setKopiert(null);
                  }
                }}
              >
                {kopiert === v.link ? 'Kopiert' : 'Link kopieren'}
              </button>
            </div>
          ))}
          <p className="note" style={{ color: 'var(--warn)' }}>
            Gespeichert ist nur eine Prüfsumme der Links. Sie lassen sich später nicht
            wiederherstellen — nur neue erzeugen.
          </p>
        </div>
      )}
    </div>
  );
}
